import type { QueryClient } from '@tanstack/react-query';
import { api, type Project, type ReadyMonthStatRow, type Session } from '@/api/client';
import {
  setProjectEntity,
} from '@/lib/projectEntityCache';
import { sortProjects } from '@/utils/sortProjects';
import {
  getActiveProjectsSnapshot,
  getArchivedProjectsSnapshot,
  getReadyMonthProjectsSnapshot,
  readyMonthProjectIdsPredicate,
  setActiveProjectsSnapshot,
  setArchivedProjectsSnapshot,
  setReadyMonthProjectsSnapshot,
  reuseProjectListIfUnchanged,
  syncProjectCollectionsSecondaryCaches,
} from '@/lib/projectListQueryCache';
import {
  markProjectsTableReconcileFinished,
  markProjectsTableReconcileStarted,
  markProjectsTableTrustStale,
  type ProjectsTableSyncReason,
} from '@/lib/projectsTableSyncPolicy';
import { reloadTableRowSummariesDiff } from '@/lib/summariesBatchReload';
import { ARCHIVED_PROJECT_IDS_QUERY_KEY } from '@/lib/projectEntityCache';

let reconcileInFlight: Promise<void> | null = null;

function normalizeTs(s: string | null | undefined): string {
  return (s ?? '').trim();
}

/** Строка таблицы «та же», если updated_at совпадает (основной критерий SSOT). */
function isProjectRowUnchanged(prev: Project, next: Project): boolean {
  if (prev === next) return true;
  const ua = normalizeTs(prev.updated_at);
  const ub = normalizeTs(next.updated_at);
  if (ua !== '' && ub !== '' && ua === ub) return true;
  return false;
}

function mergeProjectPreserveRef(prev: Project | undefined, next: Project): Project {
  if (prev && isProjectRowUnchanged(prev, next)) return prev;
  return next;
}

/**
 * Серверный порядок — sortProjects(server); элементы с тем же id и updated_at сохраняют ссылку из prev.
 */
export function mergeProjectListsPreservingRefs(
  prev: Project[] | undefined,
  server: Project[]
): Project[] {
  const prevById = new Map((prev ?? []).map((p) => [p.id, p]));
  const sorted = sortProjects(server);
  const next = sorted.map((s) => mergeProjectPreserveRef(prevById.get(s.id), s));
  return reuseProjectListIfUnchanged(prev, next) ?? next;
}

function mergeReadyMonthSummariesPreservingRefs(
  prev: ReadyMonthStatRow[] | undefined,
  server: ReadyMonthStatRow[]
): ReadyMonthStatRow[] {
  if (prev && prev.length === server.length) {
    let allSameRef = true;
    const next = server.map((s, i) => {
      const p = prev[i];
      if (p && p.year_month === s.year_month) return p;
      allSameRef = false;
      return s;
    });
    if (allSameRef) return prev;
    if (next.every((row, i) => row === prev[i])) return prev;
    return next;
  }
  return server;
}

function toActiveList(data: Awaited<ReturnType<typeof api.projects.list>>): Project[] {
  const raw = Array.isArray(data) ? data : data.items;
  return raw;
}

/** Обновляет `['project', id]` только если в кэше появилась новая ссылка на проект. */
function syncProjectDetailCache(queryClient: QueryClient, merged: Project[]): void {
  for (const p of merged) {
    setProjectEntity(queryClient, p);
  }
}

/**
 * После разрыва WS или ошибки мутации: тихий фетч снимков и атомарный патч кэша
 * с сохранением ссылок на неизменённые строки.
 */
export type ReconcileProjectsTableOptions = {
  reason?: ProjectsTableSyncReason;
};

export async function reconcileProjectsTableFromServer(
  queryClient: QueryClient,
  options?: ReconcileProjectsTableOptions
): Promise<void> {
  if (reconcileInFlight) return reconcileInFlight;

  const reason = options?.reason ?? 'background';
  reconcileInFlight = (async () => {
    markProjectsTableReconcileStarted(queryClient, reason);
    try {
      const [activeRes, summariesRes] = await Promise.all([
        api.projects.list({ excludeReady: true }),
        api.projects.readyMonths(),
      ]);

      const serverActive = toActiveList(activeRes);
      const prevTableActive = getActiveProjectsSnapshot(queryClient);
      const mergedActive = mergeProjectListsPreservingRefs(prevTableActive, serverActive);

      setActiveProjectsSnapshot(queryClient, mergedActive);
      syncProjectDetailCache(queryClient, mergedActive);

      const prevSummaries = queryClient.getQueryData<ReadyMonthStatRow[]>(['ready-month-summaries']);
      const mergedSummaries = mergeReadyMonthSummariesPreservingRefs(prevSummaries, summariesRes);
      queryClient.setQueryData<ReadyMonthStatRow[]>(['ready-month-summaries'], mergedSummaries);

      const readyEntries = queryClient.getQueryCache().findAll({ predicate: readyMonthProjectIdsPredicate });
      const monthJobs: Promise<void>[] = [];

      for (const q of readyEntries) {
        const key = q.queryKey as unknown[];
        if (!Array.isArray(key) || key.length < 3) continue;
        const y = key[1];
        const m = key[2];
        if (typeof y !== 'number' || typeof m !== 'number') continue;
        if (q.state.data === undefined) continue;

        monthJobs.push(
          (async () => {
            try {
              const fresh = await api.projects.readyByMonth(y, m);
              const prevMonth = getReadyMonthProjectsSnapshot(queryClient, { year: y, month: m });
              const mergedMonth = mergeProjectListsPreservingRefs(prevMonth, fresh);
              setReadyMonthProjectsSnapshot(queryClient, { year: y, month: m }, mergedMonth);
              syncProjectDetailCache(queryClient, mergedMonth);
            } catch {
              /* оставляем кэш; следующий reconcile или сокет обновят */
            }
          })()
        );
      }

      await Promise.all(monthJobs);

      const archivedIdsCached = queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY);
      if (archivedIdsCached !== undefined) {
        try {
          const freshArchived = await api.projects.archivedList();
          const prevArch = getArchivedProjectsSnapshot(queryClient);
          const mergedArch = mergeProjectListsPreservingRefs(prevArch, freshArchived);
          setArchivedProjectsSnapshot(queryClient, mergedArch);
          syncProjectDetailCache(queryClient, mergedArch);
        } catch {
          /* сеть */
        }
      }

      syncProjectCollectionsSecondaryCaches(queryClient);
      await reconcileSessionRangeQueriesFromServer(queryClient);
      await reloadTableRowSummariesDiff(queryClient);
      markProjectsTableReconcileFinished(queryClient, reason);
    } catch (error) {
      markProjectsTableTrustStale(queryClient);
      throw error;
    } finally {
      reconcileInFlight = null;
    }
  })();

  return reconcileInFlight;
}

function sessionUnchanged(prev: Session, next: Session): boolean {
  if (prev === next) return true;
  const ua = normalizeTs(prev.updated_at);
  const ub = normalizeTs(next.updated_at);
  if (ua !== '' && ub !== '' && ua === ub) return true;
  return false;
}

function mergeSessionListsPreservingRefs(
  prev: Session[] | undefined,
  server: Session[]
): Session[] {
  const prevById = new Map((prev ?? []).map((s) => [s.id, s]));
  const next = server.map((s) => {
    const old = prevById.get(s.id);
    if (old && sessionUnchanged(old, s)) return old;
    return s;
  });
  if (prev && prev.length === next.length && prev.every((p, i) => p === next[i])) return prev;
  return next;
}

/** Расписание: `['sessions', from, to]` — дифф после возможного разрыва соединения. */
export async function reconcileSessionRangeQueriesFromServer(queryClient: QueryClient): Promise<void> {
  const entries = queryClient.getQueriesData<Session[]>({
    predicate: (q) => {
      const k = q.queryKey as unknown[];
      return Array.isArray(k) && k[0] === 'sessions' && k.length === 3;
    },
  });

  await Promise.all(
    entries.map(async ([queryKey, prev]) => {
      const k = queryKey as unknown[];
      const from = k[1];
      const to = k[2];
      if (typeof from !== 'string' || typeof to !== 'string') return;
      try {
        const fresh = await api.sessions.list({ from, to });
        const merged = mergeSessionListsPreservingRefs(prev, fresh);
        queryClient.setQueryData<Session[]>(queryKey, merged);
      } catch {
        /* сеть */
      }
    })
  );
}
